import { CirclePlus, UserPlus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { StatusBadge } from "../components/common/StatusBadge";
import { SummaryCard } from "../components/common/SummaryCard";
import { TablePagination } from "../components/common/TablePagination";
import { useSyncChanges } from "../hooks/useSyncEngine";
import { extractErrorMessage, fetchDashboard } from "../lib/api";
import { formatDateTime } from "../lib/dateTime";
import { getVisibleLoadStatus } from "../lib/statuses";

const DEFAULT_PAGE_SIZE = 30;

export const DashboardPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("week");
  const [recentLoadsCurrentPage, setRecentLoadsCurrentPage] = useState(1);
  const [recentLoadsPageSize, setRecentLoadsPageSize] = useState(DEFAULT_PAGE_SIZE);

  const loadDashboard = useCallback(async () => {
    try {
      const payload = await fetchDashboard({ scope });
      setDashboard(payload);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to load dashboard."));
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useSyncChanges(["load"], () => loadDashboard());

  const stats = dashboard?.stats;
  const scopeLabel = stats?.scope_label || "Current Week";
  const paginatedRecentLoads = useMemo(() => (dashboard?.recent_loads || []).slice((recentLoadsCurrentPage - 1) * recentLoadsPageSize, recentLoadsCurrentPage * recentLoadsPageSize), [dashboard?.recent_loads, recentLoadsCurrentPage, recentLoadsPageSize]);

  useEffect(() => {
    setRecentLoadsCurrentPage(1);
  }, [dashboard?.recent_loads?.length, recentLoadsPageSize, scope]);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">{scopeLabel} overview</p>
          <h1 className="mt-2 text-4xl font-semibold text-zinc-900">Control room dashboard</h1>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="inline-flex rounded-2xl border border-zinc-200 bg-white p-1" data-testid="dashboard-scope-toggle">
            <button className={`rounded-2xl px-4 py-3 text-sm font-semibold transition ${scope === "day" ? "bg-zinc-900 text-white" : "text-zinc-600"}`} data-testid="dashboard-scope-day-button" onClick={() => setScope("day")} type="button">Today</button>
            <button className={`rounded-2xl px-4 py-3 text-sm font-semibold transition ${scope === "week" ? "bg-zinc-900 text-white" : "text-zinc-600"}`} data-testid="dashboard-scope-week-button" onClick={() => setScope("week")} type="button">This Week</button>
          </div>
          <Link
            className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-zinc-800"
            data-testid="dashboard-create-load-link"
            to="/loads"
          >
            <CirclePlus className="h-4 w-4" />
            Create load
          </Link>
          <Link
            className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-5 py-3 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900"
            data-testid="dashboard-add-customer-link"
            to="/customers"
          >
            <UserPlus className="h-4 w-4" />
            Add customer
          </Link>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard accent="zinc" helperText={`${scopeLabel}: assigned, unassigned, and in transit loads.`} label="Total loads" testId="dashboard-card-total-loads" value={loading ? "..." : stats?.total_loads || 0} />
        <SummaryCard accent="orange" helperText={`${scopeLabel} loads still missing a truck, trailer, or driver.`} label="Unassigned" testId="dashboard-card-unassigned" value={loading ? "..." : stats?.unassigned_loads || 0} />
        <SummaryCard accent="blue" helperText={`${scopeLabel} loads fully assigned and ready to roll.`} label="Assigned" testId="dashboard-card-booked" value={loading ? "..." : stats?.assigned_loads || 0} />
        <SummaryCard accent="orange" helperText={`${scopeLabel} freight currently moving.`} label="In Transit" testId="dashboard-card-in-transit" value={loading ? "..." : stats?.in_transit_loads || 0} />
        <SummaryCard accent="green" helperText={`${scopeLabel} completed deliveries.`} label="Delivered" testId="dashboard-card-delivered" value={loading ? "..." : stats?.delivered_loads || 0} />
        <SummaryCard accent="purple" helperText={`${scopeLabel} loads already invoiced.`} label="Invoiced" testId="dashboard-card-invoiced" value={loading ? "..." : stats?.invoiced_loads || 0} />
        <SummaryCard accent="blue" helperText="Trucks ready for dispatch." label="Active trucks" testId="dashboard-card-active-trucks" value={loading ? "..." : stats?.total_active_trucks || 0} />
      </section>

      <section className="table-shell" data-testid="dashboard-recent-loads-section">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
          <div>
            <h2 className="text-2xl font-semibold text-zinc-900">Recent active loads</h2>
          </div>
          <Link className="text-sm font-semibold text-zinc-700 transition hover:text-zinc-900" data-testid="dashboard-view-all-loads-link" to="/loads">
            View all loads
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="data-table" data-testid="dashboard-recent-loads-table">
            <thead>
              <tr>
                <th>Load</th>
                <th>Customer</th>
                <th>Pickup</th>
                <th>Delivery</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="py-10 text-center text-sm text-zinc-500" colSpan={5} data-testid="dashboard-recent-loads-loading">
                    Loading recent loads...
                  </td>
                </tr>
              ) : dashboard?.recent_loads?.length ? (
                paginatedRecentLoads.map((load) => (
                  <tr key={load.id} data-testid={`dashboard-recent-load-row-${load.id}`}>
                    <td>
                      <p className="font-semibold text-zinc-900">{load.load_number}</p>
                      <p className="mt-1 text-xs text-zinc-500">{load.driver_name || "No driver yet"}</p>
                    </td>
                    <td>{load.customer_name}</td>
                    <td>
                      <p>{load.pickup_company}</p>
                      <p className="mt-1 text-xs text-zinc-500">{formatDateTime(load.pickup_datetime)}</p>
                    </td>
                    <td>
                      <p>{load.delivery_company}</p>
                      <p className="mt-1 text-xs text-zinc-500">{formatDateTime(load.delivery_datetime)}</p>
                    </td>
                    <td>
                      <StatusBadge status={getVisibleLoadStatus(load)} testId={`dashboard-status-${load.id}`} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="py-10 text-center text-sm text-zinc-500" colSpan={5} data-testid="dashboard-recent-loads-empty">
                    No recent loads found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination currentPage={recentLoadsCurrentPage} pageSize={recentLoadsPageSize} setCurrentPage={setRecentLoadsCurrentPage} setPageSize={setRecentLoadsPageSize} testIdPrefix="dashboard-recent-loads" totalItems={dashboard?.recent_loads?.length || 0} />
      </section>

      <section className="panel-surface px-5 py-5" data-testid="dashboard-upcoming-activity-card">
          <h2 className="text-2xl font-semibold text-zinc-900">Upcoming activity</h2>
          <div className="mt-4 grid gap-6 md:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Pickups</p>
              <div className="mt-3 space-y-2">
                {(dashboard?.upcoming_pickups || []).length === 0 ? <p className="text-sm text-zinc-400" data-testid="dashboard-upcoming-pickups-empty">No upcoming pickups on active loads.</p> : null}
                {(dashboard?.upcoming_pickups || []).map((load) => (
                  <p className="flex items-baseline justify-between gap-3 text-sm text-zinc-700" data-testid={`dashboard-upcoming-pickup-${load.id}`} key={load.id}>
                    <span>{load.load_number} — {load.pickup_city}</span>
                    <span className="shrink-0 text-xs text-zinc-500">{formatDateTime(load.pickup_datetime)}</span>
                  </p>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Deliveries</p>
              <div className="mt-3 space-y-2">
                {(dashboard?.upcoming_deliveries || []).length === 0 ? <p className="text-sm text-zinc-400" data-testid="dashboard-upcoming-deliveries-empty">No upcoming deliveries on active loads.</p> : null}
                {(dashboard?.upcoming_deliveries || []).map((load) => (
                  <p className="flex items-baseline justify-between gap-3 text-sm text-zinc-700" data-testid={`dashboard-upcoming-delivery-${load.id}`} key={load.id}>
                    <span>{load.load_number} — {load.delivery_city}</span>
                    <span className="shrink-0 text-xs text-zinc-500">{formatDateTime(load.delivery_datetime)}</span>
                  </p>
                ))}
              </div>
            </div>
          </div>
      </section>
    </div>
  );
};